import React from 'react';
import socketIOClient, { Socket } from 'socket.io-client';
import { gql } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';

import EventPageLoader from './EventPageLoader';
import MapEvent from './MapEvent/MapEvent';
import Timer from './Timer/Timer';
import NotStartedEventMap from './NotStartedEventMap';
import EventHeader from './EventHeader/EventHeader';
import { CompetitorType, TrackPoint, useEventContext } from 'providers/EventProvider';
import { getStoredCompetitorColor } from 'utils/competitorColors';

import Box from '@mui/material/Box';
import { toast } from 'react-toastify';

export const drawerWidth = 320;

const gqlCompetitorsQuery = gql`
  query GetSubEventCompetitors($slug: String!, $sub: String!) {
    eventCompetitors: getLiveEvent(slug: $slug, sub: $sub) {
      id
      slug
      sub
      competitors {
        id
        deviceName
        trackCompress
      }
    }
  }
`;

const GQL_LIVE_DEVICES_DATA = gql`
  query GetPublicEventDevices($names: [String!]!, $slug: String, $from: Date) {
    devices: getPublicEventDevices(names: $names, slug: $slug, from: $from) {
      name
      points
    }
  }
`;

const Event = () => {
  const { event, updatePlayers } = useEventContext();

  const [loading, setLoading] = React.useState(true);
  const [loadingCompetitors, setLoadingCompetitors] = React.useState(true);
  const [status, setStatus] = React.useState<string>();

  const [from, setFrom] = React.useState<number>();
  const [connected, setConnected] = React.useState(false);
  const socketRef = React.useRef<Socket | undefined>(undefined);

  const [open, setOpen] = React.useState(window.innerWidth >= 768);
  const shellRef = React.useRef<HTMLDivElement>(null);

  const [getCompetitorsData, { data: competitorsData, error: competitorsError }] = useLazyQuery<{
    eventCompetitors: { competitors: Array<CompetitorType> };
  }>(gqlCompetitorsQuery);

  const [getLiveCompetitorsData, { data: liveCompetitorsData }] = useLazyQuery<{
    devices: Array<{ name: string; points: Array<number[]> }>;
  }>(GQL_LIVE_DEVICES_DATA);

  React.useEffect(() => {
    if (!competitorsData) return;

    const { eventCompetitors } = competitorsData;
    const startEvent = new Date(event.startDate).getTime();
    updatePlayers((players: CompetitorType[]) => {
      eventCompetitors.competitors.forEach((element) => {
        const player = players.filter((p) => p.deviceName === element.deviceName)[0];
        player.track = element.trackCompress.map((p) => ({
          time: p[0] * 1000 + startEvent,
          lat: p[1],
          lng: p[2]
        }));
      });
      return [...players];
    });
    setLoadingCompetitors(false);
  }, [competitorsData, event.startDate, updatePlayers]);

  React.useEffect(() => {
    if (competitorsError) {
      setLoadingCompetitors(false);
    }
  }, [competitorsError]);

  React.useEffect(() => {
    if (!liveCompetitorsData) return;

    const { devices } = liveCompetitorsData;
    const startEvent = new Date(event.startDate).getTime();
    updatePlayers((players) => {
      players.forEach((p) => p.track.sort((a, b) => a.time - b.time));
      devices.forEach((element) => {
        const player = players.find((p) => p.deviceName === element.name);
        if (!player) return;
        player.track = [
          ...player.track,
          ...element.points.map((p) => ({
            time: p[0] * 1000 + startEvent,
            lat: p[1],
            lng: p[2]
          }))
        ];
      });
      players.forEach((p) => p.track.sort((a, b) => a.time - b.time));
      return [...players];
    });
    setLoadingCompetitors(false);
    toast.dismiss();
  }, [liveCompetitorsData, event.startDate, updatePlayers]);

  React.useEffect(() => {
    if (event.status === 'hidden' && !event.isOpen) {
      setStatus('upcoming');
      setLoading(false);
    } else {
      const dataPlayers: CompetitorType[] = JSON.parse(JSON.stringify(event.competitors));
      dataPlayers.forEach((p) => {
        const storedColor = getStoredCompetitorColor(p.deviceName);
        if (storedColor) p.color = storedColor;
        p.track = [];
        p.offsetTime = new Date(p.startTime).getTime();
        p.options = {
          show: true,
          fullRoute: false,
          highlight: false
        };
      });
      const start = new Date(event.startDate).getTime();
      // const end = new Date(event.endDate).getTime();
      setFrom(start);

      updatePlayers(dataPlayers);
      setLoading(false);

      if (event.isLive) {
        getLiveCompetitorsData({
          variables: {
            from,
            slug: event.slug,
            names: event.competitors.map((competitor) => competitor.deviceName)
          }
        });

        if (socketRef.current) {
          socketRef.current.close();
        }
        socketRef.current = socketIOClient(import.meta.env.VITE_DOMAIN, {
          path: import.meta.env.VITE_API_SOCKET
        });
      } else {
        getCompetitorsData({
          variables: {
            slug: event.slug,
            sub: event.sub
          }
        });
      }
    }
  }, [event.slug, event.sub, event.status, event.isOpen, event.isLive, event.startDate, updatePlayers]);

  React.useEffect(() => {
    const addPointsToPlayer = (data: { track: TrackPoint[]; device: string }) => {
      updatePlayers((players) => {
        const player = players.find((p) => p.deviceName === data.device);
        if (player) {
          data.track.forEach((d) => player.track.push(d));
          player.track.sort((a, b) => a.time - b.time);
        }
        return [...players];
      });
    };

    const addBuffPointsToPlayer = (data: { track: TrackPoint[]; device: string }) => {
      updatePlayers((players) => {
        const player = players.find((p) => p.deviceName === data.device);
        if (player) {
          data.track.forEach((d) => player.track.push(d));
          player.track.sort((a, b) => a.time - b.time);
        }
        return [...players];
      });
    };

    if (socketRef.current) {
      socketRef.current.on('connect', () => {
        toast.dismiss();
        setConnected(true);
      });
      socketRef.current.on(event.slug + '_' + event.sub, addPointsToPlayer);
      socketRef.current.on('buff_' + event.slug + '_' + event.sub, addBuffPointsToPlayer);
      socketRef.current.on('announce_' + event.slug + '_' + event.sub, (data) => {
        if (data.type === 'refresh') {
          window.location.reload();
        }
      });
      socketRef.current.on('disconnect', () => {
        toast.info('Lost connection.', {
          autoClose: false,
          style: { backgroundColor: '#ff0066', color: '#ffffff' }
        });
        console.log('disconnect');
        setFrom(new Date().getTime());
        setConnected(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketRef]);

  React.useEffect(() => {
    if (connected) {
      console.log('connect');

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  if (loading) return <EventPageLoader />;

  if (status === 'upcoming') {
    return <NotStartedEventMap event={event} />;
  }

  return (
    <Box className="event-shell" ref={shellRef}>
      <EventHeader shellRef={shellRef} />
      <Box component="main" sx={{ flex: '1 1 auto', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ flex: '1 1 auto', minHeight: 0, position: 'relative' }}>
          <MapEvent loading={loadingCompetitors} open={open} setOpen={setOpen} />
        </Box>
        <Timer />
      </Box>
    </Box>
  );
};
export default Event;
